class Hero extends HTMLElement {
  constructor() {
    super();
    this.parent = null;

    // ── Spinning-O animation state ────────────────────────────────
    this._scrollY = 0;
    this._last = null;
    this._rafId = null;
    this._hovered = false;
    this._running = false;
    this._currentSpeed = 0; // lerps toward target speed or 0
  }

  connectedCallback() {
    this.parent = this;

    this.setComponentHeight();

    window.addEventListener(
      "resize",
      debounce(() => this.setComponentHeight(), 200),
    );

    this._initLogoAnimation();
  }

  disconnectedCallback() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  // ── Utility ──────────────────────────────────────────────────────

  setComponentHeight() {
    const header = document.querySelector("header.header");
    if (header) {
      const offsetHeight = window.innerHeight - header.clientHeight;
      this.style.setProperty("--offset-height", `${offsetHeight}px`);
    }
  }

  // ── Spinning-O logo animation ─────────────────────────────────────

  _initLogoAnimation() {
    const logoEl = this.querySelector("#lhLogo");
    const wheelGroup = this.querySelector("#lhWheel");

    if (!logoEl || !wheelGroup) return;

    // O geometry (measured from the original SVG file)
    const CX = 571.59;
    const R_OUTER = 144.3;
    const R_INNER = 101.47;
    const R_MID = (R_OUTER + R_INNER) / 2; // 122.885
    const SW = R_OUTER - R_INNER; // 42.83

    // Clip window spans LORE top → HOUSE bottom (579 SVG units)
    const CLIP_TOP = 458;
    const CLIP_BOT = 1037;
    const CLIP_H = CLIP_BOT - CLIP_TOP; // 579

    // Slot spacing: one full O diameter + gap
    const GAP = 20;
    const SPACING = R_MID * 2 + SW + GAP; // ≈ 308 SVG units
    const LOOP = SPACING;

    // How quickly currentSpeed lerps toward its target.
    // Lower = smoother/slower transition. 0.04 ≈ ~0.5s ease-in, ~1s ease-out.
    const EASE_IN = 0.04;
    const EASE_OUT = 0.02;

    // Build enough circle elements to fill the clip window
    const COUNT = Math.ceil(CLIP_H / SPACING) + 3;
    const circles = [];

    for (let i = 0; i < COUNT; i++) {
      const c = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "circle",
      );
      c.setAttribute("r", R_MID);
      c.setAttribute("fill", "none");
      c.setAttribute("stroke", "#fff");
      c.setAttribute("stroke-width", SW);
      wheelGroup.appendChild(c);
      circles.push(c);
    }

    const drawFrame = () => {
      const offset = this._scrollY % SPACING;
      circles.forEach((c, i) => {
        const oy = CLIP_TOP + offset + (i - 1) * SPACING;
        c.setAttribute("cx", CX);
        c.setAttribute("cy", oy);
      });
    };

    const tick = (ts) => {
      if (!this._last) this._last = ts;
      const dt = Math.min(ts - this._last, 50);
      this._last = ts;

      // Read target speed from CSS custom property (set by Shopify section setting)
      const rawSpeed = parseFloat(
        getComputedStyle(logoEl).getPropertyValue("--lh-speed") || 2,
      );
      const targetSpeed = Math.max(0.1, Math.min(rawSpeed, 10));

      if (this._hovered) {
        // Ease current speed up toward the target
        this._currentSpeed += (targetSpeed - this._currentSpeed) * EASE_IN;
      } else {
        // Ease current speed down toward zero
        this._currentSpeed += (0 - this._currentSpeed) * EASE_OUT;
      }

      // Advance the scroll position by however fast we're currently moving
      this._scrollY = (this._scrollY + this._currentSpeed * 0.1 * dt) % LOOP;

      drawFrame();

      // Stop the RAF loop only once we've fully come to rest
      if (!this._hovered && this._currentSpeed < 0.01) {
        this._currentSpeed = 0;
        this._running = false;
        drawFrame();
        return;
      }

      this._rafId = requestAnimationFrame(tick);
    };

    // Hover listeners
    logoEl.addEventListener("mouseenter", () => {
      this._hovered = true;
      if (!this._running) {
        this._running = true;
        this._last = null;
        this._rafId = requestAnimationFrame(tick);
      }
    });

    logoEl.addEventListener("mouseleave", () => {
      this._hovered = false;
      // tick() keeps running and eases speed down to 0 before stopping itself
    });

    // Paint first frame immediately (no flash on load)
    drawFrame();
  }
}

// ── Module-scoped debounce ────────────────────────────────────────────
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

customElements.define("component-hero", Hero);
